import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  HostBinding,
  HostListener,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { merge, chunk, max, min, sum, uniq, uniqBy, isNil } from 'lodash';
import { from, Observable, of, Subscription } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';

import {
  ColorService,
  ColumnOrderEntry,
  DEFAULT_EXCLUDE_FALSY_TOOGLE,
  Dimension,
  DimensionProperty,
  Match,
  Metric,
  MetricValue,
  MetricValues,
  Result,
  BenchmarkService,
  DatesService,
  Query,
  QueryFilter,
  RequestOptionsArgs,
  ROOT_CONNECTIVE,
  rootConnective,
  DateRangeValue,
  DynamicDateRange,
  Rollup,
  Slice,
  Row,
  MessageService,
  EMPTY_FALSY_ERROR,
  TrackingService,
  WidgetQueueProxyService,
  WrapMiddleService,
} from '@hub-components';
import { MetricFormat } from 'app/apps/studio/app/models/dashboard.model';
import { PageQuerySigningService } from 'app/apps/studio/app/services/page-query-signing.service';
import { BenchmarkSelection } from 'app/hub-components/components/benchmarks/benchmarks.component';
import { DEFAULT_TABLE_TEXT_FONT_SIZE, DEFAULT_RESULTS_PER_PAGE, DEFAULT_COMPARE_TO, DEFAULT_SHOW_COLUMN_GRAND_TOTALS, DEFAULT_SHOW_ROW_GRAND_TOTALS, DEFAULT_SHOW_COLUMN_ALL_PAGES_TOTALS } from '../../../../constants';
import { MetricTotal } from '../../interfaces/metric-total.interface';
import { WidgetStyle, WidgetStyleOptions } from '../../interfaces/widget-style.interface';
import { ChartOptions } from '../chart-options';
import { resolveDashboardMetricFormat } from '../widget-metric-format.util';
type MetricComparison = 'green_increase' | 'red_increase';

type CopiedRow = { isHeader: boolean; cells: string[] };
type CopiedTable = { title: string | null; rows: CopiedRow[] };

type TableColumn =
  | { id: string; kind: 'rollup' }
  | { id: string; kind: 'dimension'; dimension: Dimension; dimensionIndex: number }
  | { id: string; kind: 'property'; dimension: Dimension; dimensionIndex: number; property: DimensionProperty; propertyIndex: number }
  | { id: string; kind: 'metric'; metric: Metric; metricIndex: number };

type TableSeries = { key: string; date?: Date; slices: Slice[]; metrics: MetricValues[] };

type PageEntry = { date?: Date; slices: Slice[] };

@Component({
  selector: 'table-widget',
  templateUrl: 'table-widget.component.html',
  styleUrls: ['table-widget.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})

export class TableWidgetComponent implements OnChanges, OnDestroy, OnInit {
  @HostBinding('style.backgroundColor') backgroundColor: string;

  @Input() result: Result;
  @Input() metrics: Metric[];
  @Input() style: WidgetStyle;
  @Input() chartOptions: ChartOptions = {};
  @Input() dashboardInterval: DynamicDateRange;
  @Input() dashboardMetricFormats: MetricFormat[] = [];
  @Input() widgetId: string;
  @Input() canReorderColumns: boolean = false;

  @Output() columnOrderChanged = new EventEmitter<{ widgetId: string; columnOrder: ColumnOrderEntry[] }>();

  dateFormat = { pattern: 'MMM d, yyyy', timezone: 'UTC' };

  static supportedOptions = [
    {
      bindTo: 'rollup',
      label: 'Sum by',
      type: 'rollupDropdown',
    },
    {
      bindTo: 'breakdown',
      label: 'Table breakdown',
      type: 'breakdown',
      options: [
        {
          bindTo: 'rowDimension',
        }, {
          bindTo: 'columnDimension',
        },
      ],
    },
    {
      bindTo: 'transpose',
      type: 'transpose',
    },
    {
      bindTo: 'limit',
      label: 'Number of results',
      type: 'limitDropdown',
    },
    {
      bindTo: 'resultsPerPage',
      label: 'Results per page',
      type: 'resultsPerPageDropdown',
    },
    {
      bindTo: 'dataOptions',
      label: 'Data',
      type: 'dataOptions',
      options: [
        {
          bindTo: 'excludeFalsy',
          label: 'Exclude N/A and 0 values by row',
        }, {
          bindTo: 'showColumnGrandTotals',
        }, {
          bindTo: 'showColumnAllPagesTotals',
        }, {
          bindTo: 'showRowGrandTotals',
        },
      ],
    },
    {
      bindTo: 'heatmap',
      label: 'Heatmap',
      type: 'checkbox',
    },
    {
      bindTo: 'title',
      label: 'Title',
      type: 'textFormat',
      options: [
        {
          bindTo: 'text',
        }, {
          bindTo: 'align',
        }, {
          bindTo: 'size',
        },
      ],
    },
    {
      bindTo: 'footer',
      label: 'Footer',
      type: 'footerCheckbox',
      options: [
        {
          bindTo: 'datasetInfo',
        },
        {
          bindTo: 'dateRange',
        },
        {
          bindTo: 'comparisonDateRange',
        },
      ],
    },
    {
      bindTo: 'tableText',
      label: 'Text',
      type: 'textFormat',
      options: [
        {
          bindTo: 'size',
        },
      ],
    },
    {
      bindTo: 'dateRange',
      label: 'Date range',
      type: 'dateRange',
      options: [
        {
          bindTo: 'enabled',
        }, {
          bindTo: 'value',
        },
      ],
    },
    {
      bindTo: 'compareTo',
      label: 'Compare To',
      type: 'compareToDropdown',
    },
    {
      bindTo: 'compareToDisplay',
      type: 'compareToDisplay',
      options: [
        {
          bindTo: 'separateColumn',
        }, {
          bindTo: 'benchmarksAs',
        }, {
          bindTo: 'groupedHeader',
        },
      ],
    },
    {
      bindTo: 'columnOrder',
      type: 'hidden',
    },
  ];

  static supportedStyle: WidgetStyleOptions[] = [
    {
      bindTo: 'backgroundColor',
      label: 'Background Color',
    },
    {
      bindTo: 'textColor',
      label: 'Text Color',
    },
    {
      bindTo: 'borderWidth',
      label: 'Thickness',
    },
    {
      bindTo: 'borderColor',
      label: 'Color',
    },
    {
      bindTo: 'shadow',
      label: 'Size',
    },
    {
      bindTo: 'shadowColor',
      label: 'Color',
    },
    {
      bindTo: 'shadowOpacity',
      label: 'Opacity',
    },
    {
      bindTo: 'borderRadius',
      label: 'Corner Radius',
    },
  ];

  static supportedRollups = ['day', 'week', 'month', 'quarter', 'year', 'total'];
  textColor: string;
  title: { [key: string]: string | number };
  colorScale: any;
  textSize: number = DEFAULT_TABLE_TEXT_FONT_SIZE;
  footer: any;
  public footerData: any;
  public comparisonDateRange: string;
  public comparisonDatePages: string[][];
  public comparisonDates: string[];

  maxValues: { [key: string]: number };
  uniqueDates: any;
  public pivoted: boolean = false;
  public columnDimension: Dimension | null = null;
  public columnDimensionSlices: Slice[] = [];
  public columnDimensionProperties: { property: DimensionProperty; propertyIndex: number }[] = [];
  public columnsPerMetric: number = 0;
  public metricColspan: number = 0;
  public dimensionHeaderRowspan: number = 2;
  public rowTotalHeaderRowspan: number | null = null;


  public pages: any;
  public resultSeries: TableSeries[];
  public uniqueSlices: any;
  public datesAsRowsTotals: MetricTotal[] = [];
  public datesAsRowsTotalsByMetric: { [metricName: string]: MetricTotal } = {};
  public datesAsColumnsColTotals: MetricTotal[] = [];
  public datesAsColumnsColTotalsByMetric: MetricTotal[][] = [];
  public datesAsColumnsRowTotals: MetricTotal[][] = [];
  public datesAsColumnsTotalOfTotals: MetricTotal[] = [];
  public showColumnGrandTotals = DEFAULT_SHOW_COLUMN_GRAND_TOTALS;
  public showRowGrandTotals = DEFAULT_SHOW_ROW_GRAND_TOTALS;
  public showColumnAllPagesTotals = DEFAULT_SHOW_COLUMN_ALL_PAGES_TOTALS;
  public orderedColumns: TableColumn[] = [];
  public orderedMetricColumns: Extract<TableColumn, { kind: 'metric' }>[] = [];
  public firstNonMetricColumnId: string | null = null;
  public spacerBeforeColumnId: string | null = null;
  public draggingId: string | null = null;
  public dropTargetId: string | null = null;
  public dragHandleArmed: boolean = false;
  public updateResult: boolean = true;
  public initialPageNum: number = 0;
  private _uniqueSlices: any;
  private _filteredUniqueSlices: any;
  private _resultData: TableSeries[];
  private _filteredResultData: TableSeries[];
  private _resultSeries: TableSeries[];
  private _pages: any;
  private _filteredPages: any;
  private _excludeFalsy = DEFAULT_EXCLUDE_FALSY_TOOGLE;
  private isFilterEmpty = false;
  private _firstLoad: boolean = true;
  private columnDimensionIndex: number = -1;
  private pageTotalsSubscription: Subscription;
  private benchmarkResult: Result | null = null;
  private benchmarkSubscription: Subscription;
  private benchmarkPageTotalsSubscription: Subscription;
  private pivotRowTotals: Map<string, MetricValue[]> | null = null;
  private pivotRowTotalsSubscription: Subscription;
  private pageTotalsComeFromEngine: boolean = false;
  private pivotRowTotalsRefused: boolean = false;
  private summedRowTotals: MetricTotal[][] = [];
  resultsPerPage = DEFAULT_RESULTS_PER_PAGE;
  compareTo: BenchmarkSelection = DEFAULT_COMPARE_TO;
  currentPageNum = 0;


  datasetDisplayName: string;

  benchmarkPages: any;
  benchmarkValuePages: any;
  benchmarkTotals: { [metricName: string]: MetricValue };
  benchmarkPageTotals: { [metricName: string]: number } = {};
  benchmarkColumnPageTotals: { [metricName: string]: number[] } = {};
  benchmarkDatesLabel: string;
  currentDatesLabel: string;
  separateColumn: boolean = false;
  groupedHeader: boolean = false;
  benchmarksAs: string = 'percentages';
  loadingBenchmarks: boolean = false;
  loadingBenchmarkPageTotals: boolean = false;

  constructor(
    colorService: ColorService,
    private changeDetectorRef: ChangeDetectorRef,
    private benchmarkService: BenchmarkService,
    private datesService: DatesService,
    private messageService: MessageService,
    private trackingService: TrackingService,
    private widgetQueueProxy: WidgetQueueProxyService,
    private wrapMiddleService: WrapMiddleService,
    private elementRef: ElementRef,
    private pageQuerySigningService: PageQuerySigningService,
  ) {
    this.colorScale = colorService.getScale(['#e57c76', '#f2b8a3', '#fff3cf', '#aed7ae', '#5abb8c']);
  }

  ngOnInit(): void {
    if (this.chartOptions?.initialPageNum) {
      this.currentPageNum = this.chartOptions.initialPageNum;
      this.initialPageNum = this.chartOptions.initialPageNum;
    }
  }

  ngOnDestroy(): void {
    this.benchmarkSubscription?.unsubscribe();
    this.pageTotalsSubscription?.unsubscribe();
    this.benchmarkPageTotalsSubscription?.unsubscribe();
    this.pivotRowTotalsSubscription?.unsubscribe();
  }

  @HostListener('copy', ['$event'])
  onCopyText(event: ClipboardEvent): void {
    this.trackingService.track('Table Widget: Copy text', { value: { widgetType: 'table' } });
    this.writeSelectedTableToClipboard(event);
  }

  ngOnChanges(changes) {
    if (this.style) {
      this.textColor = this.style.textColor;
      this.backgroundColor = this.style.backgroundColor;
    }

    if (this.result) {
      this.updateBreakdown();
    }

    if (this.result?.series) {
      this._resultSeries = this.tableSeries(this.result);
      this._resultData = this.getResultsSeriesData(this._resultSeries, false);
      this._filteredResultData = this.getResultsSeriesData(this._resultSeries, true);
      this.isFilterEmpty = this._filteredResultData.length === 0 && this._resultSeries.length !== 0;
      if (this.isFilterEmpty && this._excludeFalsy) {
        this.messageService.info(EMPTY_FALSY_ERROR);
      }
      this.updateResultSeriesData();
    }

    if (this.chartOptions) {
      this.addTracking(changes);

      const compareToDisplay = this.chartOptions.compareToDisplay;
      this.separateColumn = !!compareToDisplay?.separateColumn &&
        !!this.chartOptions.compareTo?.value &&
        this.chartOptions.compareTo.value !== 'none' &&
        this.result?.query?.rollup?.value === 'total' &&
        this.chartOptions.transpose !== 'columns';
      this.benchmarksAs = (this.separateColumn && compareToDisplay?.benchmarksAs?.value) || 'percentages';
      this.groupedHeader = this.separateColumn && !!compareToDisplay?.groupedHeader;
      this.metricColspan = this.separateColumn ? this.columnsPerMetric * 2 : this.columnsPerMetric;

      const extraHeaderRows = this.columnDimensionProperties.length + (this.separateColumn ? 1 : 0);
      this.dimensionHeaderRowspan = 2 + extraHeaderRows;
      this.rowTotalHeaderRowspan = extraHeaderRows ? 1 + extraHeaderRows : null;

      this.footer = this.chartOptions.footer;
      this.footerData = {
        datasetInfo: this.result?.query?.schema?.displayName,
        dateRange: this.datesService.intervalDateRange(this.dashboardInterval, this.result?.query?.firstDayOfWeek ? Number(this.result.query.firstDayOfWeek) : undefined),
      };

      if (this.chartOptions.title) {
        this.title = this.chartOptions.title;
        this.title.align = this.chartOptions.title.align || 'center';
      }
      if (this.chartOptions.tableText) {
        this.textSize = this.chartOptions.tableText.size;
      }

      if (this.chartOptions.dataOptions) {
        const excludeFalsyHasChanged = this.hasNewChanges(changes.options?.previousValue?.dataOptions.excludeFalsy, changes.options?.currentValue?.dataOptions.excludeFalsy);
        const showColumnGrandTotalsHasChanged = this.hasNewChanges(changes.options?.previousValue?.dataOptions.showColumnGrandTotals, changes.options?.currentValue?.dataOptions.showColumnGrandTotals);
        const showRowGrandTotalsHasChanged = this.hasNewChanges(changes.options?.previousValue?.dataOptions.showRowGrandTotals, changes.options?.currentValue?.dataOptions.showRowGrandTotals);
        const showColumnAllPagesTotalsHasChanged = this.hasNewChanges(changes.options?.previousValue?.dataOptions.showColumnAllPagesTotals, changes.options?.currentValue?.dataOptions.showColumnAllPagesTotals);

        if (excludeFalsyHasChanged) {
          if (!this._excludeFalsy && this.isFilterEmpty) {
            this.messageService.info(EMPTY_FALSY_ERROR);
          }
          this._excludeFalsy = this.chartOptions.dataOptions.excludeFalsy;
          this.updateSeriesData();
          this.updateSlicesData();
          this.updateResultSeriesData();
          this.resetToFirstPage();
        }

        if (showColumnGrandTotalsHasChanged) {
          this.showColumnGrandTotals = this.chartOptions.dataOptions.showColumnGrandTotals;
          this.resetToFirstPage();
        }

        if (showRowGrandTotalsHasChanged) {
          this.showRowGrandTotals = this.chartOptions.dataOptions.showRowGrandTotals;
          this.resetToFirstPage();
        }

        if (showColumnAllPagesTotalsHasChanged) {
          this.showColumnAllPagesTotals = this.chartOptions.dataOptions.showColumnAllPagesTotals;
          this.resetToFirstPage();
        }
      }

      if (this.chartOptions.resultsPerPage) {
        const resultsPerPageHasChanged = this.hasNewChanges(changes.options?.previousValue?.resultsPerPage, changes.options?.currentValue?.resultsPerPage);
        if (resultsPerPageHasChanged) {
          this.resultsPerPage = this.chartOptions.resultsPerPage.value;
          this.resetToFirstPage();
        }
      }

      if (this.chartOptions.compareTo) {
        this.compareTo = {
          name: this.chartOptions.compareTo.name,
          value: this.chartOptions.compareTo.value,
          dateRange: this.chartOptions.compareTo.dateRange,
          query: this.chartOptions.compareTo.query,
        };
        if (this.result && this.result.query) {
          const dateRange = this.chartOptions.dateRange && this.chartOptions.dateRange.enabled ? this.chartOptions.dateRange.value : this.dashboardInterval;
          const { startDate, endDate } = this.datesService.parseValue(dateRange, this.result.query.firstDayOfWeek ? Number(this.result.query.firstDayOfWeek) : undefined);
          const dateRangeInvalid = !this.datesService.isValid(startDate, endDate);

          if (!dateRangeInvalid) {
            this.currentDatesLabel = this.dateRangeLabel(startDate, endDate);
            const benchmarkDates = this.getBenchmarkDates(dateRange);
            this.comparisonDateRange = benchmarkDates ? this.datesService.intervalDateRange(benchmarkDates) : undefined;

            if (benchmarkDates) {
              this.loadingBenchmarks = true;
              this.benchmarkResult = null;
              this.benchmarkDatesLabel = this.dateRangeLabel(benchmarkDates.startDate, benchmarkDates.endDate);
              const benchmarkBase = this.result.query.clone();
              benchmarkBase.interval = this.intervalFromDates(benchmarkDates);
              benchmarkBase.auth = this.chartOptions.compareTo.query && this.chartOptions.compareTo.query['auth'];
              this.chartOptions.compareTo.query = merge({}, benchmarkBase.asJson(), { auth: benchmarkBase.auth });
              const benchmarkResults = this.runQuery(benchmarkBase);
              this.updateBenchmarkPages(benchmarkResults);
            } else {
              this.comparisonDates = undefined;
              this.comparisonDatePages = undefined;
            }
          }
        }
      }
    }

    if (this.result?.rows?.length && this.chartOptions?.heatmap) {
      this.maxValues = {};
      for (const index in this.result.metrics) {
        const metricName = this.result.rows[0].metrics[index].name;
        const values = this.result.rows.map(row => row.metrics[index].value);
        const maxValue = max(values.filter(v => v !== null));
        this.maxValues[metricName] = maxValue;
      }
    }

    if (this.result) {
      this.datasetDisplayName = <string> this.result.query.schema.displayName;
      const transposeHasChanged = this.hasNewChanges(changes.options?.previousValue?.transpose, changes.options?.currentValue?.transpose);
      const breakdownHasChanged = this.hasNewChanges(changes.options?.previousValue?.breakdown, changes.options?.currentValue?.breakdown);
      if ((transposeHasChanged || breakdownHasChanged) && !this._firstLoad) {
        this.resetToFirstPage();
      }

      if (this.result?.query?.rollup?.value === 'month') {
        this.dateFormat = { pattern: 'MMM yyyy', timezone: 'UTC' };
      } else if (this.result?.query?.rollup?.value === 'year') {
        this.dateFormat = { pattern: 'yyyy', timezone: 'UTC' };
      } else {
        this.dateFormat = { pattern: 'MMM d, yyyy', timezone: 'UTC' };
      }

      this.buildOrderedColumns();
    }

    if (this.result?.rows) {
      const resultHasChanged = this.hasNewChanges(changes.result?.previousValue, changes.result?.currentValue);
      if (resultHasChanged || this.updateResult) {
        this._pages = chunk(this.getRowsData(this.result.rows, false), this.resultsPerPage).map(group => group.filter(item => item !== null));
        this._filteredPages = chunk(this.getRowsData(this.result.rows, true), this.resultsPerPage).map(group => group.filter(item => item !== null));
        this._uniqueSlices = this.getSlicesData(false);
        this._filteredUniqueSlices = this.getSlicesData(true);
        this.updateSeriesData();
        this.updateSlicesData();
        this.updateResultSeriesData();
        this.loadPivotRowTotals();

        this.updateResult = false;

        if (!this._firstLoad && this.currentPageNum !== 0) {
          this.updateCurrentPageNum(0);
        } else if (this.showColumnGrandTotals || this.showRowGrandTotals) {
          this.calculateGrandTotals();
        }
        this._firstLoad = false;
      }
    }

    if (changes.dashboardMetricFormats && this.result?.rows?.length) {
      if (this.showColumnGrandTotals || this.showRowGrandTotals) {
        this.calculateGrandTotals();
      }
    }
  }

  public getMetricFormat(metricName: string, baseFormat: any): any {
    return resolveDashboardMetricFormat(metricName, baseFormat, this.result?.query?.dataset, this.dashboardMetricFormats);
  }

  public isWrapped(text: string): boolean {
    return this.wrapMiddleService.wrapString(text).includes('\n');
  }

  public armDrag(): void {
    this.dragHandleArmed = true;
    this.changeDetectorRef.markForCheck();
  }

  @HostListener('document:mouseup')
  public disarmDrag(): void {
    if (this.dragHandleArmed) {
      this.dragHandleArmed = false;
      this.changeDetectorRef.markForCheck();
    }
  }

  public onDragStart(event: DragEvent, column: TableColumn): void {
    if (!this.canReorderColumns || !this.dragHandleArmed) {
      event.preventDefault();
      return;
    }
    this.draggingId = column.id;
    event.dataTransfer?.setData('text/plain', column.id);
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
    }
  }

  public onDragOver(event: DragEvent, column: TableColumn): void {
    if (this.draggingId && this.draggingId !== column.id) {
      event.preventDefault();
      this.dropTargetId = column.id;
      this.changeDetectorRef.markForCheck();
    }
  }

  public onDragLeave(column: TableColumn): void {
    if (this.dropTargetId === column.id) {
      this.dropTargetId = null;
      this.changeDetectorRef.markForCheck();
    }
  }

  public onDrop(event: DragEvent, target: TableColumn): void {
    event.preventDefault();
    const sourceId = this.draggingId;
    this.clearDragState();
    if (sourceId && sourceId !== target.id) {
      this.reorderColumn(sourceId, target.id);
    }
  }

  public onDragEnd(): void {
    this.clearDragState();
  }

  private clearDragState(): void {
    this.draggingId = null;
    this.dropTargetId = null;
    this.dragHandleArmed = false;
    this.changeDetectorRef.markForCheck();
  }

  private reorderColumn(sourceId: string, targetId: string): void {
    const src = this.orderedColumns.findIndex(c => c.id === sourceId);
    const dst = this.orderedColumns.findIndex(c => c.id === targetId);
    if (src < 0 || dst < 0) {
      return;
    }

    const next = [...this.orderedColumns];
    const [moved] = next.splice(src, 1);
    next.splice(dst, 0, moved);

    this.orderedColumns = next;
    this.updateDerivedFields();

    const columnOrder = this.isCanonicalOrder(next)
      ? []
      : next.map(col => TableWidgetComponent.toOrderEntry(col));
    this.chartOptions.columnOrder = columnOrder;

    this.trackingService.track('Table Widget: Reorder column', { value: { widgetType: 'table' } });
    this.columnOrderChanged.emit({ widgetId: this.widgetId, columnOrder });
    this.changeDetectorRef.markForCheck();
  }

  private buildOrderedColumns(): void {
    if (!this.result) {
      return;
    }

    const canonical = this.buildCanonicalColumns();

    let ordered: TableColumn[];
    if (this.chartOptions?.columnOrder?.length) {
      ordered = [];
      const remaining = [...canonical];
      for (const entry of this.chartOptions.columnOrder) {
        const idx = remaining.findIndex(c => TableWidgetComponent.entryMatchesColumn(entry, c));
        if (idx >= 0) {
          ordered.push(remaining.splice(idx, 1)[0]);
        }
      }
      ordered = ordered.concat(remaining);
    } else {
      ordered = canonical;
    }

    this.orderedColumns = ordered;
    this.updateDerivedFields();
  }

  private updateDerivedFields(): void {
    this.orderedMetricColumns = this.orderedColumns.filter(
      (col): col is Extract<TableColumn, { kind: 'metric' }> => col.kind === 'metric',
    );
    this.firstNonMetricColumnId = this.orderedColumns.find(col => col.kind !== 'metric')?.id ?? null;

    const firstMetricIndex = this.orderedColumns.findIndex(col => col.kind === 'metric');
    this.spacerBeforeColumnId = firstMetricIndex > 0 ? this.orderedColumns[firstMetricIndex].id : null;
  }

  private buildCanonicalColumns(): TableColumn[] {
    const cols: TableColumn[] = [];
    const rowDimensionName = this.chartOptions?.breakdown?.rowDimension?.id;
    const rowDimensions = this.result.dimensions.filter(dimension => dimension !== this.columnDimension);
    const leadingDimension = rowDimensions.find(dimension => dimension.name === rowDimensionName);

    if (leadingDimension) {
      this.pushDimensionColumns(cols, leadingDimension);
    }

    if (this.chartOptions?.transpose !== 'columns' && this.result.query?.rollup?.value !== 'total') {
      cols.push({ id: 'rollup', kind: 'rollup' });
    }

    rowDimensions
      .filter(dimension => dimension !== leadingDimension)
      .forEach(dimension => this.pushDimensionColumns(cols, dimension));

    this.result.metrics.forEach((metric, metricIndex) => {
      cols.push({ id: `metric:${metric.name}`, kind: 'metric', metric, metricIndex });
    });

    return cols;
  }

  private pushDimensionColumns(cols: TableColumn[], dimension: Dimension): void {
    const dimensionIndex = this.result.dimensions.indexOf(dimension);
    cols.push({ id: `dim:${dimension.name}`, kind: 'dimension', dimension, dimensionIndex });

    (dimension.properties || []).forEach((property, enumIndex) => {
      cols.push({
        id: `prop:${dimension.name}.${property.name}`,
        kind: 'property',
        dimension,
        dimensionIndex,
        property,
        propertyIndex: this.dimensionPropertyIndex(dimension, property, enumIndex),
      });
    });
  }

  private dimensionPropertyIndex(dimension: Dimension, property: DimensionProperty, enumIndex: number): number {
    const sliceOrder: string[] = this.result.query?.dimensionProperties?.[dimension.name] ?? [];
    const fromSlice = sliceOrder.indexOf(property.name);

    return fromSlice >= 0 ? fromSlice : enumIndex;
  }

  private isCanonicalOrder(columns: TableColumn[]): boolean {
    const canonical = this.buildCanonicalColumns();
    return columns.length === canonical.length
      && columns.every((col, index) => col.id === canonical[index].id);
  }

  private static toOrderEntry(col: TableColumn): ColumnOrderEntry {
    switch (col.kind) {
      case 'rollup':    return { type: 'rollup' };
      case 'dimension': return { type: 'dimension', dimension: col.dimension.name };
      case 'property':  return { type: 'property', dimension: col.dimension.name, property: col.property.name };
      case 'metric':    return { type: 'metric', metric: col.metric.name };
    }
  }

  private static entryMatchesColumn(entry: ColumnOrderEntry, col: TableColumn): boolean {
    switch (entry.type) {
      case 'rollup':    return col.kind === 'rollup';
      case 'dimension': return col.kind === 'dimension' && col.dimension.name === entry.dimension;
      case 'property':  return col.kind === 'property' && col.dimension.name === entry.dimension && col.property.name === entry.property;
      case 'metric':    return col.kind === 'metric' && col.metric.name === entry.metric;
    }
  }

  private calculateGrandTotals(): void {
    this.datesAsColumnsRowTotals = [];
    this.datesAsColumnsColTotals = [];
    const datesAsRowsTotals = {};
    const startIndex = this.currentPageNum * this.resultsPerPage;
    const endIndex = startIndex + this.resultsPerPage;

    if (!this.pivoted && this.result?.query?.rollup?.value !== 'total') {
      const pageRows = this.pages[this.currentPageNum] || [];
      pageRows.forEach(row => {
        row.metrics.forEach(metric => {
          const customMetricFormat = this.getMetricFormat(metric.name, metric.format);
          const hide = this.hidesSummedTotal(customMetricFormat);
          if (!datesAsRowsTotals[metric.name]) {
            datesAsRowsTotals[metric.name] = { value: 0, format: customMetricFormat, hide: hide };
          }
          datesAsRowsTotals[metric.name].value += metric.value || 0;
        });
      });
      this.datesAsRowsTotals = Object.values(datesAsRowsTotals || {});
    } else {
      const pageResultSeries = this.resultSeries.slice(startIndex, endIndex);
      pageResultSeries.forEach(serie => {
        let rowTotals = [];
        let index = 0;
        serie.metrics.forEach(metric => {
          const customMetricFormat = this.getMetricFormat(metric.name, metric.format);
          const hide = this.hidesSummedTotal(customMetricFormat);
          datesAsRowsTotals[metric.name] = { value: (datesAsRowsTotals[metric.name] && datesAsRowsTotals[metric.name].value || 0) + metric.aggregate, format: customMetricFormat, hide: hide };
          rowTotals.push({ value: metric.aggregate, format: customMetricFormat, hide: hide && isNil(metric.aggregate) });
          metric.values.forEach(value => {
            if (this.datesAsColumnsColTotals[index]) {
              this.datesAsColumnsColTotals[index]['value'] += Number(value) || 0;
            } else {
              this.datesAsColumnsColTotals[index] = { value: Number(value) || 0, format: customMetricFormat, hide: hide };
            }
            index++;
          });
        });
        this.datesAsColumnsRowTotals.push(rowTotals);
      });
      this.datesAsRowsTotals = Object.values(datesAsRowsTotals || {});
    }

    this.summedRowTotals = [...this.datesAsColumnsRowTotals];
    this.setEngineRowTotals();
    this.sumRowTotals();
    this.datesAsRowsTotalsByMetric = datesAsRowsTotals;
    this.datesAsColumnsColTotalsByMetric = chunk(this.datesAsColumnsColTotals, this.columnsPerMetric || 1);
    this.loadPageTotals();
  }

  private sumRowTotals(): void {
    this.datesAsColumnsTotalOfTotals = [];
    this.datesAsColumnsRowTotals.forEach(rowTotal => {
      rowTotal.forEach((metric, index) => {
        if (this.datesAsColumnsTotalOfTotals[index]) {
          this.datesAsColumnsTotalOfTotals[index]['value'] += metric['value'] || 0;
        } else {
          this.datesAsColumnsTotalOfTotals[index] = { value: metric['value'] || 0, format: metric['format'], hide: this.hidesSummedTotal(metric['format']) || metric['hide'] };
        }
      });
    });
  }

  private pivotRowTotalsComeFromEngine(): boolean {
    return this.showRowGrandTotals && !!this.columnDimension && this.columnDimensionSlices.length > 0 && this.resultIsComplete(this.result) && !this.pivotRowTotalsRefused;
  }

  private loadPivotRowTotals(): void {
    this.pivotRowTotalsSubscription?.unsubscribe();
    this.pivotRowTotals = null;
    this.pivotRowTotalsRefused = false;

    if (!this.pivotRowTotalsComeFromEngine()) {
      return;
    }

    const query = this.result.query.clone();
    query.dimensions = query.dimensions.filter(dimension => dimension.name !== this.columnDimension.name);
    query.groupBy = query.groupBy?.filter(dimension => dimension?.name !== this.columnDimension.name);
    query.dimensionProperties = {};
    query.order = [];
    this.pivotRowTotalsSubscription = this.runPageQuery(this.result.query, query).subscribe(rowTotalsResult => {
      if (!rowTotalsResult) {
        this.pivotRowTotalsRefused = true;
        this.datesAsColumnsRowTotals = [...this.summedRowTotals];
      } else {
        this.setPivotRowTotals(rowTotalsResult);
      }

      if (!this.pageTotalsComeFromEngine) {
        this.sumRowTotals();
      }
      this.changeDetectorRef.markForCheck();
    });
  }

  private setPivotRowTotals(rowTotalsResult: Result): void {
    const rowDimensionNames = this.result.dimensions.filter(dimension => dimension !== this.columnDimension).map(dimension => dimension.name);
    this.pivotRowTotals = new Map(rowTotalsResult.rows.map(row => [
      [this.result.timestamps.indexOf(row.date.getTime()), ...rowDimensionNames.map(name => row.slices.find(slice => slice.dimension.name === name)?.id)].join('-'),
      row.metrics,
    ]));
    this.setEngineRowTotals();
  }

  private setEngineRowTotals(): void {
    if (!this.pivotRowTotalsComeFromEngine()) {
      return;
    }

    const startIndex = this.currentPageNum * this.resultsPerPage;
    this.resultSeries.slice(startIndex, startIndex + this.resultsPerPage).forEach((serie, rowIndex) => {
      this.datesAsColumnsRowTotals[rowIndex] = this.result.metrics.map(metric => {
        const customMetricFormat = this.getMetricFormat(metric.name, metric.format);
        if (this.sumsItsTotal(metric)) {
          const aggregate = serie.metrics.find(serieMetric => serieMetric.name === metric.name)?.aggregate;

          return { value: aggregate, format: customMetricFormat, hide: this.hidesSummedTotal(customMetricFormat) && isNil(aggregate) };
        }

        const value = this.pivotRowTotals?.get(serie.key)?.find(rowTotal => rowTotal.name === metric.name)?.value ?? null;

        return { value, format: customMetricFormat, hide: !this.pivotRowTotals || this.hidesSummedTotal(customMetricFormat) };
      });
    });
  }

  private loadPageTotals(): void {
    this.pageTotalsSubscription?.unsubscribe();
    const query = this.showColumnGrandTotals ? this.pageTotalsQuery(this.result) : null;
    this.pageTotalsComeFromEngine = !!query;

    if (query) {
      const summedPageTotals = { ...this.datesAsRowsTotalsByMetric };
      const summedColumnPageTotals = [...this.datesAsColumnsColTotalsByMetric];
      this.setEnginePageTotals(null);
      this.pageTotalsSubscription = this.runPageQuery(this.result.query, query).subscribe(pageResult => {
        if (pageResult) {
          this.setEnginePageTotals(pageResult);
        } else {
          this.pageTotalsComeFromEngine = false;
          this.datesAsRowsTotalsByMetric = summedPageTotals;
          this.datesAsColumnsColTotalsByMetric = summedColumnPageTotals;
          this.sumRowTotals();
        }
        this.changeDetectorRef.markForCheck();
      });
    }
  }

  private setEnginePageTotals(pageResult: Result | null): void {
    this.result.metrics.forEach((metric, index) => {
      if (this.sumsItsTotal(metric)) {
        return;
      }

      const customMetricFormat = this.getMetricFormat(metric.name, metric.format);
      const total: MetricTotal = { value: pageResult?.totals[metric.name]?.value ?? null, format: customMetricFormat, hide: !pageResult || this.hidesSummedTotal(customMetricFormat) };

      if (this.pivoted) {
        const columnValues = pageResult ? this.pageColumnValues(pageResult, metric.name) : Array(this.columnsPerMetric).fill(null);
        this.datesAsColumnsColTotalsByMetric[index] = columnValues.map(value => ({ ...total, value }));
        this.datesAsColumnsTotalOfTotals[index] = total;
      } else {
        this.datesAsRowsTotalsByMetric[metric.name] = total;
      }
    });
  }

  private pageTotalsQuery(baseResult: Result): Query | null {
    if (rootConnective(baseResult.query.filters) !== ROOT_CONNECTIVE || (this.runsThroughALink(baseResult.query) && baseResult.query.metricFilters.length)) {
      return null;
    }

    const pageEntries = this.currentPageEntries();
    const rowDimensions = this.pageRowDimensions(baseResult.query);
    const sliceIds = this.pageSliceIds(pageEntries, rowDimensions);
    const dateIndexes = this.pageDateIndexes(pageEntries);

    if (!this.selectionMatchesPage(baseResult, pageEntries, rowDimensions, sliceIds, dateIndexes)) {
      return null;
    }

    const query = baseResult.query.clone();
    query.dimensionProperties = {};
    query.metricFilters = [];
    query.order = [];
    query.filters = [
      ...query.filters,
      ...rowDimensions.map((dimension, index) => ({
        expressions: this.pageFilterExpressions(dimension, sliceIds[index]),
      })),
    ];

    if (this.pivoted) {
      query.dimensions = query.dimensions.filter(dimension => dimension.name === this.columnDimension?.name);
      query.groupBy = null;
    }

    if (this.columnDimension) {
      query.rollup = new Rollup('total');
    }

    if (this.datesAreRows()) {
      query.interval = this.pageInterval(baseResult.query.interval, min(dateIndexes), max(dateIndexes));
    }

    return query;
  }

  private pageFilterExpressions(dimension: Dimension, ids: string[]): QueryFilter[] {
    const values = ids.filter(Boolean).map(id => new Match(id, id, []));

    return [
      ...(values.length ? [new QueryFilter({ type: 'any', values, dimension })] : []),
      ...(ids.some(id => !id) ? [new QueryFilter({ type: 'na', values: [], dimension })] : []),
    ];
  }

  private datesAreRows(): boolean {
    const datesAreColumns = this.pivoted && !this.columnDimension;

    return !datesAreColumns && this.result.query.rollup.value !== 'total';
  }

  private currentPageEntries(): PageEntry[] {
    const startIndex = this.currentPageNum * this.resultsPerPage;

    return this.datesAreRows() && !this.pivoted ? this.pages[this.currentPageNum] || [] : this.resultSeries.slice(startIndex, startIndex + this.resultsPerPage);
  }

  private pageRowDimensions(query: Query): Dimension[] {
    const cubeDimensionNames = (query.groupBy || query.dimensions).map(dimension => dimension?.name);

    return this.result.dimensions.filter(dimension => cubeDimensionNames.includes(dimension.name) && dimension.name !== this.columnDimension?.name);
  }

  private pageSliceIds(pageEntries: PageEntry[], rowDimensions: Dimension[]): string[][] {
    return rowDimensions.map(dimension => {
      const dimensionIndex = this.result.dimensions.indexOf(dimension);

      return uniq(pageEntries.map(entry => entry.slices[dimensionIndex].id));
    });
  }

  private pageDateIndexes(pageEntries: PageEntry[]): number[] {
    return this.datesAreRows() ? uniq(pageEntries.map(entry => this.result.timestamps.indexOf(entry.date.getTime()))) : [];
  }

  private selectionMatchesPage(baseResult: Result, pageEntries: PageEntry[], rowDimensions: Dimension[], sliceIds: string[][], dateIndexes: number[]): boolean {
    if (!pageEntries.length || (this.columnDimension && !this.resultIsComplete(baseResult))) {
      return false;
    }

    const dimensionIndexes = rowDimensions.map(dimension => this.result.dimensions.indexOf(dimension));
    const entryKey = (entry: PageEntry): string => JSON.stringify([
      this.datesAreRows() ? this.result.timestamps.indexOf(entry.date.getTime()) : null,
      ...dimensionIndexes.map(dimensionIndex => entry.slices[dimensionIndex].id),
    ]);
    const pageKeys = uniq(pageEntries.map(entryKey));
    const gridSize = sliceIds.reduce((size, ids) => size * ids.length, dateIndexes.length || 1);
    const datesAreConsecutive = !dateIndexes.length || max(dateIndexes) - min(dateIndexes) + 1 === dateIndexes.length;

    if (pageKeys.length === gridSize && datesAreConsecutive) {
      return true;
    }

    if (!this.resultIsComplete(baseResult)) {
      return false;
    }

    const resultSeries = baseResult === this.result ? this._resultSeries : this.tableSeries(baseResult);
    const resultEntries: PageEntry[] = this.datesAreRows() && !this.pivoted ? baseResult.rows : resultSeries;

    return resultEntries
      .filter(entry => dimensionIndexes.every((dimensionIndex, index) => sliceIds[index].includes(entry.slices[dimensionIndex].id)))
      .every(entry => pageKeys.includes(entryKey(entry)));
  }

  private resultIsComplete(result: Result): boolean {
    return !result.query.metricFilters.length && result.series.length < result.query.limit;
  }

  private pageInterval(interval: string, firstIndex: number, lastIndex: number): string {
    const timestamps = this.result.timestamps;
    const [intervalStart, intervalEnd] = interval.split('-');
    const pageStart = firstIndex === 0 ? intervalStart : formatInTimeZone(timestamps[firstIndex], 'UTC', 'yyyyMMdd');
    const pageEnd = lastIndex === timestamps.length - 1 ? intervalEnd : formatInTimeZone(timestamps[lastIndex + 1] - 1, 'UTC', 'yyyyMMdd');

    return `${pageStart}-${pageEnd}`;
  }

  private pageColumnValues(pageResult: Result, metricName: string): number[] {
    if (this.columnDimension) {
      return this.columnDimensionSlices.map(columnSlice => {
        const row = pageResult.rows.find(pageRow => pageRow.slices.some(slice => slice.dimension.name === columnSlice.dimension.name && slice.id === columnSlice.id));

        return row?.metrics.find(metric => metric.name === metricName)?.value ?? null;
      });
    }

    return pageResult.series[0]?.metrics.find(metric => metric.name === metricName)?.values ?? Array(this.columnsPerMetric).fill(null);
  }

  pageTotalsAreAddedUp(): boolean {
    return this.showColumnGrandTotals && !this.pageTotalsComeFromEngine && !!this.result?.metrics.some(metric => !this.sumsItsTotal(metric));
  }

  totalIsLoading(total: MetricTotal): boolean {
    return !!total?.hide && !this.hidesSummedTotal(total.format);
  }

  private sumsItsTotal(metric: Metric): boolean {
    return metric.meta?.display?.total === 'sum';
  }

  private hidesSummedTotal(format: any): boolean {
    const baseFormat = format?.['format'] || format;

    return baseFormat === 'percent' || baseFormat === 'date';
  }

  private hasNewChanges(previousValue, newValue): boolean {
    return JSON.stringify(previousValue) !== JSON.stringify(newValue);
  }

  private resetToFirstPage(): void {
    this.updateResult = true;
    this.currentPageNum = 0;
    this.chartOptions.initialPageNum = 0;
  }

  private getRowsData(resultRows: Row[], excludeFalsy: boolean): Row[] {
    return resultRows.filter(row => excludeFalsy ? row.metrics.some(metric => !!metric.value) : true);
  }

  private getSlicesData(excludeFalsy: boolean) {
    const series = excludeFalsy ? this._filteredResultData : this._resultData;
    return chunk(series.map(serie => serie.slices), this.resultsPerPage);
  }

  private updateSlicesData() {
    if (this._excludeFalsy) {
      this.uniqueSlices = this._filteredUniqueSlices;
    } else {
      this.uniqueSlices = this._uniqueSlices;
    }
  }

  private updateSeriesData() {
    if (this._excludeFalsy) {
      this.pages = this._filteredPages;
    } else {
      this.pages = this._pages;
    }
  }

  private updateResultSeriesData() {
    if (this._excludeFalsy) {
      this.resultSeries = this._filteredResultData;
    } else {
      this.resultSeries = this._resultData;
    }
  }

  private getResultsSeriesData(resultSeries: TableSeries[], excludeFalsy: boolean): TableSeries[] {
    return resultSeries.filter(serie =>
      excludeFalsy ? serie.metrics.some(metric => metric.values.some(value => !!value)) : true,
    );
  }

  conditionalColor(index, metric) {
    if (!this.chartOptions || !this.chartOptions.heatmap) {
      return;
    }

    const max = this.maxValues[metric.name];
    if (max !== null) {
      let metricValue;
      if (this.pivoted) {
        metricValue = metric.values[index];
      } else {
        metricValue = metric.value;
      }
      if (!metricValue) {
        return '#ffffff';
      }
      let v = metricValue / max;
      if (this.getComparison(metric) === 'red_increase') {
        v = 1.0 - v;
      }
      const color = this.colorScale(v);
      return color;
    }
  }

  getComparison(metric): MetricComparison {
    if (
      metric.metric &&
      metric.metric.meta &&
      metric.metric.meta.display &&
      metric.metric.meta.display.format &&
      metric.metric.meta.display.format.comparison
    ) {
      return metric.metric.meta.display.format.comparison;
    }
    return 'green_increase';
  }

  getBenchmarkClassNames(comparison: MetricComparison, value: number) {
    if (value === 0) {
      return '';
    }
    if ((value > 0 && comparison === 'green_increase') || (value < 0 && comparison === 'red_increase') ) {
      return 'benchmark__green';
    }
    return 'benchmark__red';
  }

  public comparisonDateTooltip(comparisonDate: string): string {
    return comparisonDate ? `Compared to ${comparisonDate}` : undefined;
  }

  updateCurrentPageNum(pageNum: number) {
    if (this.currentPageNum !== pageNum) {
      this.currentPageNum = pageNum;
      this.chartOptions.initialPageNum = this.currentPageNum;
      this.calculateGrandTotals();
      this.calculateBenchmarkPageTotals();
      this.changeDetectorRef.markForCheck();
    }
  }

  benchmarkDifference(rowIndex: number, metricName: string, value: number, valueIndex?: number): number {
    const benchmarkRow = this.benchmarkValuePages?.[this.currentPageNum]?.[rowIndex]?.[metricName];
    const benchmarkValue = valueIndex === undefined ? benchmarkRow : benchmarkRow?.[valueIndex];

    if (value !== null && benchmarkValue > 0) {
      return value - benchmarkValue;
    }
  }

  totalBenchmark(value: number, benchmarkValue: number): number {
    return this.benchmarkService.diff(value ?? null, benchmarkValue);
  }

  totalDifference(value: number, benchmarkValue: number): number {
    if (value !== null && value !== undefined && benchmarkValue > 0) {
      return value - benchmarkValue;
    }
  }

  private calculateBenchmarkPageTotals(): void {
    const pageBenchmarks = this.benchmarkValuePages?.[this.currentPageNum] || [];

    if (this.pivoted) {
      const totals: { [metricName: string]: number[] } = {};
      pageBenchmarks.forEach(benchmarkRow => {
        Object.entries(benchmarkRow).forEach(([metricName, values]: [string, number[]]) => {
          totals[metricName] = values.map((value, index) => (totals[metricName]?.[index] || 0) + (Number(value) || 0));
        });
      });
      this.benchmarkColumnPageTotals = totals;
    } else {
      const totals: { [metricName: string]: number } = {};
      pageBenchmarks.forEach(benchmarkRow => {
        Object.entries(benchmarkRow).forEach(([metricName, value]) => {
          totals[metricName] = (totals[metricName] || 0) + (Number(value) || 0);
        });
      });
      this.benchmarkPageTotals = totals;
    }

    this.loadBenchmarkPageTotals();
  }

  private loadBenchmarkPageTotals(): void {
    this.benchmarkPageTotalsSubscription?.unsubscribe();
    const query = this.showColumnGrandTotals && this.separateColumn && this.benchmarkResult ? this.pageTotalsQuery(this.benchmarkResult) : null;
    this.loadingBenchmarkPageTotals = !!query;

    if (query) {
      this.benchmarkPageTotalsSubscription = this.runPageQuery(this.benchmarkResult.query, query, this.result.query).subscribe(pageResult => {
        if (pageResult) {
          this.setEngineBenchmarkPageTotals(pageResult);
        }
        this.loadingBenchmarkPageTotals = false;
        this.changeDetectorRef.markForCheck();
      });
    }
  }

  private setEngineBenchmarkPageTotals(pageResult: Result): void {
    this.result.metrics.filter(metric => !this.sumsItsTotal(metric)).forEach(metric => {
      if (this.pivoted) {
        this.benchmarkColumnPageTotals[metric.name] = this.pageColumnValues(pageResult, metric.name);
      } else {
        this.benchmarkPageTotals[metric.name] = pageResult.totals[metric.name]?.value ?? null;
      }
    });
  }

  getNbrOfPages() {
    if (this.pivoted) {
      return this.uniqueSlices.length;
    } else {
      return this.pages.length;
    }
  }

  private updateBenchmarkPages(benchmarkResults) {
    this.benchmarkSubscription = from(benchmarkResults).subscribe((b: Result) => {
      this.benchmarkResult = b;
      this.benchmarkTotals = <{ [metricName: string]: MetricValue }> b.totals;
      if (this.pivoted) {
        const benchSeriesData = this.getResultsSeriesData(this.tableSeries(b), this._excludeFalsy);
        const benchmarksBySlices = benchSeriesData.reduce((memo, series) => {
          memo[series.key] = series.metrics.reduce((values, metric) => {
            values[metric.name] = metric.values;
            return values;
          }, {});
          return memo;
        }, {});
        const benchmarks = this.resultSeries.map(series => benchmarksBySlices[series.key]);

        if (benchmarks && benchmarks.length) {
          this.benchmarkPages = this.benchmarkService.createBenchmarkPages(this.resultSeries, benchmarks, this.resultsPerPage, 'columns');
          this.benchmarkValuePages = chunk(benchmarks.map(benchmark => benchmark || {}), this.resultsPerPage);
          this.calculateBenchmarkPageTotals();
          this.comparisonDates = this.columnDimension ? undefined : b.rows.slice(0, b.timestamps.length)
            .map(row => formatInTimeZone(row.date, 'UTC', this.dateFormat.pattern));
          this.comparisonDatePages = this.columnDimension && this.result.query.rollup.value !== 'total'
            ? chunk(this.resultSeries.map(series => this.comparisonDateFor(series.date, b)), this.resultsPerPage)
            : undefined;
          this.loadingBenchmarks = false;
          this.changeDetectorRef.markForCheck();
        }
      } else {
        const rowsData = this.getRowsData(this.result.rows, this._excludeFalsy);
        const benchmarksBySlices = b.rows.reduce((memo, row) => {
          const key = row.slices.map(s => s.id).join('-');
          if (!memo[key]) {
            memo[key] = {};
          }
          const date = row.date.getTime();
          const values = row.metrics.reduce((memo, metric) => {
            memo[metric.name] = metric.value;
            return memo;
          }, {});
          memo[key][date] = values;
          return memo;
        }, {});

        const resultDates = this.benchmarkService.getSortedUniqueDatesFromRows(rowsData);
        const benchmarkDates = this.benchmarkService.getSortedUniqueDatesFromRows(b.rows);
        const comparisonDates = rowsData.map(row => benchmarkDates[resultDates.indexOf(row.date.getTime().toString())]);
        const benchmarks = rowsData.map((row, index) => {
          const key = row.slices.map(s => s.id).join('-');
          return benchmarksBySlices[key]?.[comparisonDates[index]];
        });

        if (benchmarks && benchmarks.length) {
          this.benchmarkPages = this.benchmarkService.createBenchmarkPages(rowsData, benchmarks, this.resultsPerPage);
          this.benchmarkValuePages = chunk(benchmarks.map(benchmark => benchmark || {}), this.resultsPerPage);
          this.calculateBenchmarkPageTotals();
          this.comparisonDatePages = this.result.query.rollup.value === 'total' ?
            undefined :
            chunk(comparisonDates.map(date => date && formatInTimeZone(new Date(Number(date)), 'UTC', this.dateFormat.pattern)), this.resultsPerPage);
          this.loadingBenchmarks = false;
          this.changeDetectorRef.markForCheck();
        }
      }
    });
  }

  private updateBreakdown(): void {
    const columnDimensionName = this.chartOptions.breakdown?.columnDimension?.id;
    this.columnDimensionIndex = this.result.dimensions.findIndex(dimension => dimension.name === columnDimensionName);
    this.columnDimension = this.result.dimensions[this.columnDimensionIndex] || null;
    this.columnDimensionSlices = this.columnDimension
      ? uniqBy(this.result.rows.map(row => row.slices[this.columnDimensionIndex]), slice => slice.id)
      : [];
    this.columnDimensionProperties = (this.columnDimension?.properties || []).map((property, enumIndex) => ({
      property,
      propertyIndex: this.dimensionPropertyIndex(this.columnDimension, property, enumIndex),
    }));
    this.pivoted = this.chartOptions.transpose === 'columns' || !!this.columnDimension;
    this.uniqueDates = this.result.rows.slice(0, this.result.timestamps.length).map(row => row.date);
    this.columnsPerMetric = this.columnDimension ? this.columnDimensionSlices.length : this.uniqueDates.length;
  }

  private comparisonDateFor(date: Date, benchmark: Result): string {
    const timestamp = benchmark.timestamps[this.result.timestamps.indexOf(date.getTime())];

    return timestamp && formatInTimeZone(new Date(timestamp), 'UTC', this.dateFormat.pattern);
  }

  private tableSeries(result: Result): TableSeries[] {
    if (this.columnDimension) {
      return this.pivotSeries(result, this.columnDimensionSlices.map(slice => slice.id));
    }

    return result.series.map(series => ({ key: series.slices.map(slice => slice.id).join('-'), slices: series.slices, metrics: series.metrics }));
  }

  private pivotSeries(result: Result, columnIds: string[]): TableSeries[] {
    const dateIndexes = new Map(result.timestamps.map((timestamp, index) => [timestamp, index]));
    const groups = new Map<string, { date: Date; slices: Slice[]; values: number[][] }>();

    result.rows.forEach(row => {
      const dateIndex = dateIndexes.get(row.date.getTime());
      const rowSliceIds = row.slices.filter((_, index) => index !== this.columnDimensionIndex).map(slice => slice.id);
      const key = [dateIndex, ...rowSliceIds].join('-');
      const valueIndex = columnIds.indexOf(row.slices[this.columnDimensionIndex].id);

      if (!groups.has(key)) {
        groups.set(key, { date: row.date, slices: row.slices, values: row.metrics.map(() => columnIds.map(() => null)) });
      }

      if (valueIndex >= 0) {
        row.metrics.forEach((metric, metricIndex) => {
          groups.get(key).values[metricIndex][valueIndex] = metric.value;
        });
      }
    });

    return Array.from(groups, ([key, group]) => ({
      key,
      date: group.date,
      slices: group.slices,
      metrics: result.metrics.map((metric, metricIndex) => new MetricValues(metric, group.values[metricIndex], this.pivotAggregate(metric, group.values[metricIndex]))),
    }));
  }

  private pivotAggregate(metric: Metric, values: number[]): number | null {
    const summable = !this.hidesSummedTotal(this.getMetricFormat(metric.name, metric.format)) && values.some(value => !isNil(value));

    return summable ? sum(values) : null;
  }

  private runPageQuery(widgetQuery: Query, pageQuery: Query, shownQuery?: Query): Observable<Result | null> {
    const signedQuery: Observable<Query> = this.runsThroughALink(widgetQuery)
      ? this.pageQuerySigningService.sign(widgetQuery, pageQuery, shownQuery).pipe(map(({ auth, selection, metric_selection }) => Object.assign(pageQuery, { auth, selection, providedMetricSelection: metric_selection })))
      : of(Object.assign(pageQuery, { auth: undefined, selection: {}, providedMetricSelection: undefined }));

    return signedQuery.pipe(
      mergeMap(query => this.runQuery(query, { throwErrors: true })),
      catchError(() => of(null)),
    );
  }

  private runsThroughALink(query: Query): boolean {
    return !!(query.publicId || query.shareId);
  }

  private runQuery(query: Query, options: RequestOptionsArgs = {}) {
    return this.widgetQueueProxy
      .analiticsQueueProxy(query, { cache: true, skipLoader: true, ...options }).pipe(
        map(qr => Result.parse(qr)),
      );
  }

  private intervalFromDates(dates) {
    return this.datesService.fixedQueryInterval(this.datesService.parseValue(dates, this.result.query.firstDayOfWeek ? Number(this.result.query.firstDayOfWeek) : undefined));
  }

  private dateRangeLabel(startDate: Date, endDate: Date): string {
    return `${format(startDate, 'MMM d, yyyy')} - ${format(endDate, 'MMM d, yyyy')}`;
  }

  private getBenchmarkDates(dates: DateRangeValue) {
    if (this.compareTo.value && this.compareTo.value !== 'none') {
      return this.benchmarkService.calculateBenchmark(dates, this.compareTo.value, this.compareTo.dateRange, this.result.query.firstDayOfWeek ? Number(this.result.query.firstDayOfWeek) : undefined);
    } else {
      return null;
    }
  }

  private addTracking(changes: SimpleChanges) {
    if (changes?.options) {
      const { currentValue, previousValue } = changes?.options;

      if (previousValue?.dataOptions && currentValue?.dataOptions) {
        if (previousValue.dataOptions.excludeFalsy !== currentValue.dataOptions.excludeFalsy) {
          this.trackingService.track('Table Widget: Exclude N/A and 0 values', { value: currentValue.dataOptions.excludeFalsy });
        }
        if (previousValue.dataOptions.showColumnGrandTotals !== undefined && previousValue.dataOptions.showColumnGrandTotals !== currentValue.dataOptions.showColumnGrandTotals) {
          this.trackingService.track('Table Widget: Show column grand totals', { value: currentValue.dataOptions.showColumnGrandTotals });
        }
        if (previousValue.dataOptions.showRowGrandTotals !== undefined && previousValue.dataOptions.showRowGrandTotals !== currentValue.dataOptions.showRowGrandTotals) {
          this.trackingService.track('Table Widget: Show row grand totals', { value: currentValue.dataOptions.showRowGrandTotals });
        }
        if (previousValue.dataOptions.showColumnAllPagesTotals !== undefined && previousValue.dataOptions.showColumnAllPagesTotals !== currentValue.dataOptions.showColumnAllPagesTotals) {
          this.trackingService.track('Table Widget: Show column all pages totals', { value: currentValue.dataOptions.showColumnAllPagesTotals });
        }
      }

      if (previousValue?.footer && currentValue?.footer) {
        if (previousValue?.footer?.dateRange !== undefined && previousValue?.footer?.dateRange !== currentValue?.footer?.dateRange) {
          this.trackingService.track('Table Widget: Footer DateRange', { value: currentValue.footer.dateRange });
        }

        if (previousValue?.footer?.datasetInfo !== currentValue?.footer?.datasetInfo) {
          this.trackingService.track('Table Widget: Footer Dataset Info', { value: currentValue.footer.datasetInfo });
        }

        if (previousValue?.footer?.comparisonDateRange !== currentValue?.footer?.comparisonDateRange) {
          this.trackingService.track('Table Widget: Footer Comparison DateRange', { value: currentValue.footer.comparisonDateRange });
        }
      }

      if (previousValue?.compareTo && currentValue?.compareTo) {
        if (previousValue?.compareTo.value !== currentValue?.compareTo.value) {
          this.trackingService.track('Table Widget: Compare To', { value: currentValue.compareTo.name, dateRange: currentValue.compareTo.dateRange });
        }
      }

      if (previousValue?.compareToDisplay && currentValue?.compareToDisplay) {
        if (previousValue.compareToDisplay.separateColumn !== currentValue.compareToDisplay.separateColumn) {
          this.trackingService.track('Table Widget: Show comparison values in a separate column', { value: currentValue.compareToDisplay.separateColumn });
        }
        if (previousValue.compareToDisplay.benchmarksAs?.value !== currentValue.compareToDisplay.benchmarksAs?.value) {
          this.trackingService.track('Table Widget: Show benchmarks as', { value: currentValue.compareToDisplay.benchmarksAs.name });
        }
      }
    }
  }

  private writeSelectedTableToClipboard(event: ClipboardEvent): void {
    if (!event?.clipboardData) {
      return;
    }

    const table = this.selectedTable();
    if (!table) {
      return;
    }

    event.preventDefault();
    event.clipboardData.setData('text/plain', TableWidgetComponent.toPlainText(table));
    event.clipboardData.setData('text/html', TableWidgetComponent.toHtml(table));
  }

  private selectedTable(): CopiedTable | null {
    const selection = document.getSelection();
    if (!selection || selection.isCollapsed || selection.rangeCount === 0) {
      return null;
    }

    const container = selection.getRangeAt(0).commonAncestorContainer;
    const element = container.nodeType === Node.ELEMENT_NODE ? (container as Element) : container.parentElement;

    if (element?.closest('td, th')) {
      return null;
    }

    const table = element?.closest('table') ?? element?.querySelector('table');
    if (!table) {
      return null;
    }

    const rows = TableWidgetComponent.buildCopiedRows(table as HTMLTableElement, selection);
    if (!rows) {
      return null;
    }

    return { title: this.selectedTitle(selection), rows };
  }

  private selectedTitle(selection: Selection): string | null {
    const titleElement: HTMLElement = this.elementRef.nativeElement.querySelector('.title');
    if (!titleElement || !selection.containsNode(titleElement, true)) {
      return null;
    }

    return titleElement.textContent?.trim() || null;
  }

  private static buildCopiedRows(table: HTMLTableElement, selection: Selection): CopiedRow[] | null {
    const grid: string[][] = [];
    const selectedRows = new Set<number>();
    const columnsWithContent = new Set<number>();
    let firstColumn = Number.MAX_SAFE_INTEGER;
    let lastColumn = -1;

    Array.from(table.rows).forEach((row, rowIndex) => {
      grid[rowIndex] = grid[rowIndex] ?? [];
      let column = 0;

      Array.from(row.cells).forEach(cell => {
        while (grid[rowIndex][column] !== undefined) {
          column++;
        }

        const isSelected = selection.containsNode(cell, true);
        if (isSelected) {
          selectedRows.add(rowIndex);
          firstColumn = Math.min(firstColumn, column);
          lastColumn = Math.max(lastColumn, column + cell.colSpan - 1);
        }

        const isSpacer = cell.classList.contains('spacer');
        for (let spannedRow = 0; spannedRow < cell.rowSpan; spannedRow++) {
          for (let spannedColumn = 0; spannedColumn < cell.colSpan; spannedColumn++) {
            grid[rowIndex + spannedRow] = grid[rowIndex + spannedRow] ?? [];
            grid[rowIndex + spannedRow][column + spannedColumn] =
              spannedRow === 0 && spannedColumn === 0 && isSelected ? TableWidgetComponent.cellText(cell) : '';
            if (!isSpacer) {
              columnsWithContent.add(column + spannedColumn);
            }
          }
        }

        column += cell.colSpan;
      });
    });

    if (lastColumn < 0) {
      return null;
    }

    const columns = TableWidgetComponent.columnRange(firstColumn, lastColumn).filter(column => columnsWithContent.has(column));

    return Array.from(table.rows)
      .map((row, rowIndex) => ({ row, rowIndex }))
      .filter(({ rowIndex }) => selectedRows.has(rowIndex))
      .map(({ row, rowIndex }) => ({
        isHeader: row.parentElement?.tagName === 'THEAD',
        cells: columns.map(column => grid[rowIndex][column] ?? ''),
      }));
  }

  private static columnRange(firstColumn: number, lastColumn: number): number[] {
    return Array.from({ length: lastColumn - firstColumn + 1 }, (_, offset) => firstColumn + offset);
  }

  private static cellText(cell: HTMLTableCellElement): string {
    const withoutLineBreaks = cell.cloneNode(true) as HTMLElement;
    withoutLineBreaks.querySelectorAll('br').forEach(lineBreak => lineBreak.replaceWith(' '));

    return (withoutLineBreaks.textContent ?? '').replace(/\s+/g, ' ').trim();
  }

  private static toPlainText({ title, rows }: CopiedTable): string {
    const lines = rows.map(row => row.cells.join('\t'));

    return (title ? [title, ...lines] : lines).join('\n');
  }

  private static toHtml({ title, rows }: CopiedTable): string {
    const markup = rows
      .map(row => {
        const tag = row.isHeader ? 'th' : 'td';
        const cells = row.cells.map(cell => `<${tag}>${TableWidgetComponent.escapeHtml(cell)}</${tag}>`).join('');
        return `<tr>${cells}</tr>`;
      })
      .join('');
    const heading = title ? `<p>${TableWidgetComponent.escapeHtml(title)}</p>` : '';

    return `${heading}<table>${markup}</table>`;
  }

  private static escapeHtml(text: string): string {
    return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }
}
